import { FACILITIES, isHourlyFacility } from '../hooks/facilityConfig';
import { AVAILABILITY_STATUS } from '../hooks/availabilityUtils';

// Time range definitions
export const TIME_RANGES = {
  morning: { start: '05:00', end: '12:00' },
  afternoon: { start: '12:00', end: '17:00' },
  evening: { start: '17:00', end: '23:00' }
};

// Apply all filters to facilities
export const applyFilters = (facilities, filters, availabilityData) => {
  let filtered = [...facilities];

  // Apply category filter
  if (filters.categories && filters.categories.length > 0) {
    filtered = filtered.filter(facility => 
      filters.categories.includes(facility.category)
    );
  }

  // Apply search filter
  if (filters.searchTerm && filters.searchTerm.length > 0) {
    const searchLower = filters.searchTerm.toLowerCase();
    filtered = filtered.filter(facility => 
      facility.name.toLowerCase().includes(searchLower) ||
      facility.nameEn.toLowerCase().includes(searchLower) ||
      facility.shortName.toLowerCase().includes(searchLower)
    );
  }

  // Apply time range filter (only for hourly facilities)
  if (filters.timeRanges && filters.timeRanges.length > 0) {
    filtered = filtered.filter(facility => {
      // Skip daily facilities for time range filtering
      if (!isHourlyFacility(facility.id)) {
        return true;
      }

      // Check if facility operates in any of the selected time ranges
      return filters.timeRanges.some(timeRangeKey => {
        const timeRange = TIME_RANGES[timeRangeKey];
        return doesFacilityOperateInTimeRange(facility, timeRange);
      });
    });
  }

  // Apply availability filter
  if (filters.showAvailableOnly && availabilityData) {
    filtered = filtered.filter(facility => {
      const facilityData = availabilityData.facilities?.[facility.id];
      if (!facilityData || !facilityData.days) return false;

      // Check if facility has any available slots
      return facilityData.days.some(day => 
        day.status === AVAILABILITY_STATUS.AVAILABLE ||
        day.status === AVAILABILITY_STATUS.PARTIALLY_AVAILABLE
      );
    });
  }

  return filtered;
};

// Check if facility operates within a time range
export const doesFacilityOperateInTimeRange = (facility, timeRange) => {
  if (!facility.openTime || !facility.closeTime) return false;

  const facilityStart = timeToMinutes(facility.openTime);
  const facilityEnd = timeToMinutes(facility.closeTime);
  const rangeStart = timeToMinutes(timeRange.start);
  const rangeEnd = timeToMinutes(timeRange.end);

  // Check if there's any overlap between facility hours and time range
  return facilityStart < rangeEnd && facilityEnd > rangeStart;
};

// Convert time string (HH:MM) to minutes since midnight
const timeToMinutes = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

// Filter facilities for a specific view (today, week, month)
export const filterFacilitiesForView = (facilities, viewType, availabilityData) => {
  if (!availabilityData) return facilities;

  return facilities.filter(facility => {
    const facilityData = availabilityData.facilities?.[facility.id];
    if (!facilityData || !facilityData.days) return true;

    // For today view, check today's availability
    if (viewType === 'today') {
      const today = new Date().toISOString().split('T')[0].replace(/-/g, '/');
      const todayData = facilityData.days.find(day => day.date === today);
      return todayData && todayData.status !== AVAILABILITY_STATUS.UNKNOWN;
    }

    // For week/month views, check if we have data
    return facilityData.days.length > 0;
  });
};

// Get filtered facilities with their availability status
export const getFilteredFacilitiesWithStatus = (filters, availabilityData, viewType) => {
  // Get all facilities as array
  const allFacilities = Object.values(FACILITIES);
  
  // Apply filters
  const filtered = applyFilters(allFacilities, filters, availabilityData);
  
  // Apply view-specific filtering if needed
  const viewFiltered = filterFacilitiesForView(filtered, viewType, availabilityData);

  // Add availability status to each facility
  return viewFiltered.map(facility => {
    const facilityData = availabilityData?.facilities?.[facility.id];
    const availabilityStatus = getFacilityAvailabilityStatus(facilityData, viewType);
    
    return {
      ...facility,
      availabilityStatus,
      hasData: !!facilityData
    };
  });
};

// Get availability status for a facility
const getFacilityAvailabilityStatus = (facilityData, viewType) => {
  if (!facilityData || !facilityData.days || facilityData.days.length === 0) {
    return AVAILABILITY_STATUS.UNKNOWN;
  }

  if (viewType === 'today') {
    const today = new Date().toISOString().split('T')[0].replace(/-/g, '/');
    const todayData = facilityData.days.find(day => day.date === today);
    return todayData?.status || AVAILABILITY_STATUS.UNKNOWN;
  }

  // For week/month views, calculate overall status
  const statuses = facilityData.days.map(day => day.status);
  
  if (statuses.every(status => status === AVAILABILITY_STATUS.AVAILABLE)) {
    return AVAILABILITY_STATUS.AVAILABLE;
  } else if (statuses.every(status => status === AVAILABILITY_STATUS.UNAVAILABLE)) {
    return AVAILABILITY_STATUS.UNAVAILABLE;
  } else {
    return AVAILABILITY_STATUS.PARTIALLY_AVAILABLE;
  }
};

// Count facilities by filter criteria
export const countFilteredFacilities = (filters, availabilityData) => {
  const allFacilities = Object.values(FACILITIES);
  const filtered = applyFilters(allFacilities, filters, availabilityData);
  
  return {
    filtered: filtered.length,
    total: allFacilities.length
  };
};

// Get active filter summary
export const getActiveFilterSummary = (filters) => {
  const active = [];
  
  if (filters.categories && filters.categories.length > 0) {
    active.push(`${filters.categories.length} categories`);
  }
  
  if (filters.searchTerm && filters.searchTerm.length > 0) {
    active.push('search');
  }
  
  if (filters.timeRanges && filters.timeRanges.length > 0) {
    active.push(`${filters.timeRanges.length} time ranges`);
  }
  
  if (filters.showAvailableOnly) {
    active.push('available only');
  }
  
  return active.join(', ');
};

// Check if any filters are active
export const hasActiveFilters = (filters) => {
  return (
    (filters.categories && filters.categories.length > 0) ||
    (filters.searchTerm && filters.searchTerm.length > 0) ||
    (filters.timeRanges && filters.timeRanges.length > 0) ||
    filters.showAvailableOnly
  );
};